import React, { FC, useEffect } from 'react';
import { SailingDay, SailingDayConflict } from '../../shared/types/sailing';
import {
  DEFAULT_HOUR_FORMAT,
  DEFAULT_TIMESTR_FORMAT,
  getLocaleDateStringUTC,
} from '../../shared/lib/time';

import { getDayLocation, getDayFirstArrival, getDayLastDeparture } from '../../shared/lib/sailing';
import { TextButton, InlineIcon, Tooltip } from '@spark-labs/avocado-ui';
import { setStorage } from '@go-client/lib/storage';
import { resolvePlannerPath } from '@go-client/routes';

export interface SailingDayProps {
  propertyId: string;
  day: SailingDay;
  conflicts: SailingDayConflict[];
  parity: 'odd' | 'even';
  currentDay: boolean;
  showAdminControls: boolean;
  sailingId: string;
  onSailingDayStar?: () => void;
  onSailingDayLock?: () => void;
  onSailingDayTemplateClick?: () => void;
  onRundownClick?: () => void;
  onContentBuilderClick?: () => void;
}

function makeIconButton(
  IconGlyph: (props: { className: string }) => JSX.Element,
  className: string,
  disable: boolean,
  callback?: () => void
) {
  return disable ? (
    <span className="px-1">
      <IconGlyph className={className} />
    </span>
  ) : (
    <TextButton className="px-1" onClick={callback}>
      <IconGlyph className={className} />
    </TextButton>
  );
}

function getTemplateName(day: SailingDay) {
  return day.template ? day.template.plan.name : 'Not Assigned';
}

const SailingDay: FC<SailingDayProps> = ({
  propertyId,
  day,
  sailingId,
  conflicts,
  parity,
  currentDay,
  showAdminControls,
  onSailingDayStar,
  onSailingDayLock,
  onSailingDayTemplateClick,
  onRundownClick,
  onContentBuilderClick,
}) => {
  useEffect(() => {
    if (currentDay) {
      setStorage('currentSailingUrl', resolvePlannerPath(propertyId, sailingId, day._id));
    }
  }, []);
  let bgColor;

  if (currentDay) {
    bgColor = 'bg-teal-light';
  } else {
    bgColor = parity === 'odd' ? `bg-grey-lighter` : `bg-white`;
  }

  let templateName = getTemplateName(day);
  // FIXME: We don't really support multiple days
  let firstArrival = getDayFirstArrival(day, DEFAULT_HOUR_FORMAT, DEFAULT_TIMESTR_FORMAT);
  let lastDeparture = getDayLastDeparture(day, DEFAULT_HOUR_FORMAT, DEFAULT_TIMESTR_FORMAT);
  return (
    <tr className={bgColor}>
      <td className="p-4 text-center align-top text-grey-darker">
        {getLocaleDateStringUTC(day.date)}
      </td>
      <td className="p-4 text-center align-top text-grey-darker">{day.name}</td>
      <td className="p-4 text-center align-top text-grey-darker">{day.voyage}</td>
      <td className="p-4 text-center align-top text-grey-darker">{getDayLocation(day)}</td>
      <td className="p-4 text-center align-top text-grey-darker">
        {firstArrival !== '' ? firstArrival : '--'}
      </td>
      <td className="p-4 text-center align-top text-grey-darker">
        {lastDeparture !== '' ? lastDeparture : '--'}
      </td>
      <td className="p-4 text-center align-top">
        {showAdminControls ? (
          <TextButton className="underline text-grey-darker" onClick={onSailingDayTemplateClick}>
            {templateName}
          </TextButton>
        ) : (
          <span className="text-grey-darker">{templateName}</span>
        )}
      </td>
      <td className="p-4 text-center align-top">
        {makeIconButton(
          ({ className }) => (
            <InlineIcon name="list" className={className} />
          ),
          'w-4 text-grey-darker',
          false,
          onRundownClick
        )}
        {makeIconButton(
          ({ className }) => (
            <InlineIcon name="newspaper" className={className} />
          ),
          'w-6 h-4 text-grey-darker',
          false,
          onContentBuilderClick
        )}
        {makeIconButton(
          ({ className }) => (
            <InlineIcon name="star" className={className} />
          ),
          `w-4 ${day.saved ? 'text-orange' : 'text-grey'}`,
          !showAdminControls,
          onSailingDayStar
        )}
      </td>
      <td className="p-4 flex items-center justify-center">
        {day.locked
          ? makeIconButton(
              ({ className }) => <InlineIcon name="lock" className={className} />,
              'w-4  text-grey-darker',
              !showAdminControls,
              onSailingDayLock
            )
          : makeIconButton(
              ({ className }) => <InlineIcon name="unlock" className={className} />,
              'w-4 text-grey',
              !showAdminControls,
              onSailingDayLock
            )}
        {conflicts.length > 0 && (
          <Tooltip position="left" text="There are event conflicts">
            <span className="px-1">
              <InlineIcon name="group" className="w-4 text-purple-darker" />
            </span>
          </Tooltip>
        )}
      </td>
    </tr>
  );
};

export default SailingDay;
